// Minimal toast system on Radix Toast.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { Toast as T } from "radix-ui";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";

export type ToastTone = "info" | "success" | "error";
interface ToastItem {
  id: number;
  title: string;
  description?: string | undefined;
  tone: ToastTone;
}

const Ctx = createContext<(t: { title: string; description?: string; tone?: ToastTone }) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: { title: string; description?: string; tone?: ToastTone }) => {
    setItems((xs) => [
      ...xs,
      { id: Date.now() + Math.random(), title: t.title, description: t.description, tone: t.tone ?? "info" },
    ]);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <Ctx.Provider value={value}>
      <T.Provider swipeDirection="right" duration={5000}>
        {children}
        {items.map((t) => (
          <T.Root
            key={t.id}
            onOpenChange={(open) => !open && setItems((xs) => xs.filter((x) => x.id !== t.id))}
            className="flex animate-pop-in items-start gap-3 rounded-[var(--radius-card)] bg-slate-900 px-4 py-3 text-sm text-white shadow-[var(--shadow-pop)]"
          >
            {t.tone === "error" ? (
              <AlertCircle aria-hidden className="mt-0.5 size-4.5 shrink-0 text-red-400" />
            ) : t.tone === "success" ? (
              <CheckCircle2 aria-hidden className="mt-0.5 size-4.5 shrink-0 text-emerald-400" />
            ) : (
              <Info aria-hidden className="mt-0.5 size-4.5 shrink-0 text-sky-400" />
            )}
            <div className="min-w-0">
              <T.Title className="font-medium">{t.title}</T.Title>
              {t.description && (
                <T.Description className="mt-0.5 text-white/75">{t.description}</T.Description>
              )}
            </div>
          </T.Root>
        ))}
        <T.Viewport className="fixed bottom-4 left-1/2 z-[60] flex w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 flex-col gap-2 outline-none" />
      </T.Provider>
    </Ctx.Provider>
  );
}

export function useToast() {
  return useContext(Ctx);
}
